/**
 * **P20 — the native radio, and the only reason iOS has BLE at all.**
 *
 * `webBluetooth.ts` is P16's radio and it is Chrome's. `navigator.bluetooth` does not exist in
 * WKWebView and does not exist in the Android System WebView either — Web Bluetooth is a *Chrome*
 * feature rather than a platform one — so inside P18's container **both** platforms run P16's
 * 24-hour window, its outbox and its reconciliation with no transport underneath them. Wrapping the
 * PWA did not merely fail to add BLE on iOS; on Android it *removed* the BLE that the Chrome PWA
 * had. This module is the leaf that answers both, and **it replaces the leaf and nothing else**:
 * the window, the credential holder, the outbox, reconciliation and §6's auto-pick are P16's and
 * are called, never copied (P20 §3).
 *
 * **The grammar is not here.** `COMMANDS`, `STATUS_QUERY` and — since R4/D225 — the whole of
 * `planExchange` (what is written, what closes the exchange, and how the frames are read) are
 * imported from `webBluetooth.ts`, so the two radios cannot disagree about what to send or about
 * what a set of frames meant. What crosses the Capacitor bridge is an ordered list of opaque
 * strings and an end marker; the Swift and Java plugins compose nothing and inspect nothing, which
 * is what keeps a firmware revision a one-file edit.
 *
 * **Reached through the injected global, never an import.** `web/` must not depend on
 * `@capacitor/core` — the shell is P6's and the container is P18's, and `native/` consumes the
 * built bundle rather than the other way round. `auth/nativeHttp.ts` (D166) and
 * `pwa/nativeContainer.ts` say the same thing about the same global for the same reason, and the
 * member read is `Capacitor.nativePromise` rather than `Capacitor.Plugins.<name>`: those proxies
 * are created by `registerPlugin` at *import* time, so in a bundle that refuses that import the
 * plugin namespace is empty and the obvious-looking call is `undefined`.
 *
 * **Three rules carried over from the web radio, unchanged:**
 *
 *  - **It never retries a password** (D84/PEC-25). One `PSW?` per session; a failure returns for
 *    the caller to answer with a re-fetch.
 *  - **It infers no presence** (D86). P20 binds no session proof, so a BLE open is a *transport*
 *    and never evidence of who is where. {@link nativeBleAdapter.inRange} answers about the radio,
 *    never about the gate.
 *  - **It logs nothing** (D85). The credential is one `console.debug` from Cache Storage via the
 *    devtools log, and it also crosses the bridge inside `writes` — so no failure path here
 *    stringifies a request, an error or a response body.
 */

import type { BleCredential } from './api'
import {
  COMMANDS,
  STATUS_QUERY,
  VENDOR_SERVICE_UUID,
  VENDOR_WRITE_CHAR_UUID,
  planExchange,
  type BleAdapter,
  type BleCommand,
  type BleFailureReason,
  type BleOpenResult,
} from './webBluetooth'

/** The JS-side plugin name. `native/src/platformRequirements.ts` asserts both halves use it. */
export const NATIVE_BLE_PLUGIN = 'SeloGateBle'

/**
 * 🔸 The pacing the web radio settled on, passed across rather than duplicated in two languages.
 * `open-bt.py` uses 150 ms between handshake writes; the vendor app uses ~100.
 */
const INTER_WRITE_DELAY_MS = 150
const CONNECT_TIMEOUT_MS = 6_000
const REPLY_WINDOW_MS = 4_000

/**
 * The three members the native bridge injects that this module reads. Duck-typed rather than
 * imported, and every one of them optional, because the same bundle runs in a browser where the
 * global is absent entirely.
 */
interface CapacitorBridge {
  getPlatform?: () => string
  isNativePlatform?: () => boolean
  nativePromise?: (pluginName: string, methodName: string, options: unknown) => Promise<unknown>
}

function bridge(): CapacitorBridge | undefined {
  return (globalThis as typeof globalThis & { Capacitor?: CapacitorBridge }).Capacitor
}

/**
 * Whether this launch runs inside P18's container with the bridge available.
 *
 * **Both platforms, deliberately — unlike D166's iOS-only HTTP reroute.** That one excluded Android
 * because Android's WebView `fetch` already works; here nothing works on either platform, so the
 * rule is the container rather than the platform. Making it depend on whether `navigator.bluetooth`
 * happened to be present would leave the app's behaviour resting on a WebView quirk that no test
 * in this repository can observe.
 */
export function usesNativeBle(): boolean {
  const capacitor = bridge()
  return capacitor?.isNativePlatform?.() === true && capacitor.nativePromise !== undefined
}

/** What the plugins answer. Both fields are read defensively: the bridge is not a type system. */
interface ExchangeResult {
  readonly frames: readonly string[]
  readonly failure?: string
}

/** The closed failure vocabulary both plugins emit, mapped onto the receipt schema's. */
const FAILURES: Record<string, BleFailureReason> = {
  'auth-failed': 'auth-failed',
  timeout: 'timeout',
  disconnected: 'disconnected',
  unavailable: 'unavailable',
}

function readResult(value: unknown): ExchangeResult {
  const record = typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {}
  const raw = record['frames']
  const frames = Array.isArray(raw) ? raw.filter((frame): frame is string => typeof frame === 'string') : []
  const reported = record['failure']
  const failure = typeof reported === 'string' ? reported : undefined
  return failure === undefined ? { frames } : { frames, failure }
}

/**
 * The real adapter.
 *
 * **The asymmetry with `webBluetoothAdapter` worth knowing about is the chooser.** Web Bluetooth's
 * `requestDevice` requires a user gesture and shows a device picker, which is why the web radio
 * cannot answer {@link BleAdapter.inRange} without prompting. Native scans silently — so this
 * adapter *could* answer that question for real. **It deliberately does not**, and the reason is
 * P20 §1: reconnect strategies, characteristic caching and pre-flight range checks all improve
 * reliability and all destroy the reading D76's reopening trigger is waiting for. The plain path is
 * measured first. `inRange` therefore answers about the **radio**, exactly as the web one does, and
 * §6's auto-pick keeps the inputs it already has.
 */
export const nativeBleAdapter: BleAdapter = {
  supported: (): boolean => usesNativeBle(),

  /**
   * Whether the radio could carry an exchange — **not whether this gate is near.**
   *
   * `false` only for a definite no (switched off, permission refused, no BLE hardware);
   * `undefined` for everything else, which includes the ordinary case. **`undefined` is not
   * `false`**: reading a not-yet-settled adapter state as "not in range" would put a false refusal
   * in front of a resident standing at their own gate.
   */
  async inRange(): Promise<boolean | undefined> {
    const capacitor = bridge()
    if (capacitor?.nativePromise === undefined) return undefined
    try {
      const answer = await capacitor.nativePromise(NATIVE_BLE_PLUGIN, 'availability', {})
      const state =
        typeof answer === 'object' && answer !== null ? (answer as { state?: unknown }).state : undefined
      if (state === 'off' || state === 'unauthorized' || state === 'unsupported') return false
      return undefined
    } catch {
      // The plugin is unreachable. That says nothing about the gate, so it says nothing.
      return undefined
    }
  },

  open(credential: BleCredential, command: BleCommand): Promise<BleOpenResult> {
    return exchange(credential, COMMANDS[command])
  },

  verify(credential: BleCredential): Promise<BleOpenResult> {
    return exchange(credential, STATUS_QUERY)
  },
}

/**
 * One BLE session across the bridge: the plugin connects, writes, collects and disconnects, and
 * this function decides what came back.
 *
 * Both `open` and `verify` go through here, so the handshake exists once — which is what makes
 * §7.8 step 4's *"the verification exercises the same path as the open"* a fact rather than an
 * intention. The only difference between them is the last string in `writes`.
 */
async function exchange(credential: BleCredential, command: string): Promise<BleOpenResult> {
  const startedAt = Date.now()
  const elapsed = (): number => Date.now() - startedAt

  const capacitor = bridge()
  if (capacitor?.nativePromise === undefined) {
    return { ok: false, reason: 'unavailable', latencyMs: elapsed() }
  }

  /*
   * The session order the vendor app uses, composed here and opaque to the plugin: `ID:<serial>`
   * first when the record carries one, then the single `PSW?`, then the one command.
   */
  const plan = planExchange(command)
  const writes = [
    ...(credential.bleSerial === undefined ? [] : [`ID:${credential.bleSerial}`]),
    `PSW?${credential.blePassword}`,
    // **R4/D225 — one entry for a verification, two for a command**, the second being the status
    // query whose answer decides an open the command itself never acknowledged. The plugin spaces
    // them with `interWriteDelayMs` like every other write and could not say what any of them mean.
    ...plan.writes,
  ]

  try {
    const answer = await capacitor.nativePromise(NATIVE_BLE_PLUGIN, 'exchange', {
      name: credential.bleName,
      serviceUuid: credential.bleServiceUuid ?? VENDOR_SERVICE_UUID,
      /*
       * **The characteristic gets the same vendor default the service already had, and the
       * asymmetry between those two lines was a defect measured on a live gate.**
       *
       * `VENDOR_WRITE_CHAR_UUID` was defined, exported, and passed to nobody: the record's
       * override was forwarded when present and otherwise the plugin was left to guess. Its guess
       * is "the first characteristic declaring `.write`", and on a real SEAV controller that is
       * **`C302`** — while the vendor's command characteristic is **`C303`**, which declares
       * `.writeWithoutResponse` and therefore sorts behind it. The write was refused, the session
       * ended as `disconnected` with no frames, and every layer above read it as a gate that did
       * not answer.
       *
       * `open-bt.py` never had this either: it defaults **both** identifiers. The property-based
       * pick in the plugin stays as the fallback for a firmware that renumbers, but it is no longer
       * the first answer — and on this controller that guess is actively wrong, since `C302` is the
       * first `.write` characteristic and refuses every write. `VENDOR_WRITE_CHAR_UUID` carries the
       * measured value and the account of how the three writable characteristics differ.
       */
      characteristicUuid: credential.bleCharUuid ?? VENDOR_WRITE_CHAR_UUID,
      writes,
      /*
       * **Which markers close the burst depends on what was asked, because the controller answers
       * in three vocabularies and each has its own terminator.** A status query returns a `STA:`
       * burst ending in `STA:End`; a command returns `PSW:Inst` and then `CMD:Ok` or `CMD:Err`,
       * with no `STA:` frame at all; and **either** can end early with `PSW:Err`, a refused
       * password, after which nothing arrives.
       *
       * **D225 appended a status query to a command and still did not add `STA:End` here** — see
       * {@link planExchange}, which owns that reasoning for both radios. A command exchange now
       * receives a `STA:` burst it must read and must not stop on: the controller's own frame comes
       * a second later than the query's reply, so ending on the reply would decide the verdict from
       * a position taken before the motor started.
       *
       * Both omissions cost the same way, and both were measured. Sending only `STA:End` for a
       * command meant the marker never arrived, so **every open — including the ones that
       * physically opened the gate — sat out the full reply window and was reported as a transport
       * `timeout`** (~6.1 s against a ~2 s exchange). Omitting `PSW:Err` did it again for a refused
       * password, and there it also **hides the refusal from `readBurst`**, because a transport
       * failure outranks the frames — so `auth-failed` never surfaces and PEC-25's re-fetch never
       * runs.
       *
       * **The grammar is still not the plugin's.** What crosses the bridge is a list of opaque
       * strings; the leaf compares prefixes and could not say what any of them mean.
       */
      endMarkers: [...plan.endMarkers],
      interWriteDelayMs: INTER_WRITE_DELAY_MS,
      connectTimeoutMs: CONNECT_TIMEOUT_MS,
      replyWindowMs: REPLY_WINDOW_MS,
    })

    const result = readResult(answer)
    if (result.failure !== undefined) {
      /*
       * **A transport failure outranks the frames, and an unknown word is `disconnected`.** The
       * plugin's vocabulary is closed and asserted on both sides, so an unrecognised value means
       * the two halves have drifted — which is a fault, and a fault must not read as a gate that
       * refused a password.
       */
      return { ok: false, reason: FAILURES[result.failure] ?? 'disconnected', latencyMs: elapsed() }
    }

    const verdict = plan.read(result.frames)
    if (verdict !== 'ok') return { ok: false, reason: verdict, latencyMs: elapsed() }
    return { ok: true, latencyMs: elapsed() }
  } catch {
    /*
     * **The error is classified and never logged**, and here there is nothing to classify *by*:
     * a rejection from the bridge carries a message Capacitor composed, on a call whose payload
     * held the gate password. `disconnected` is the honest reading — the plugin resolves every
     * outcome it can name, so a rejection means the call did not complete.
     */
    return { ok: false, reason: 'disconnected', latencyMs: elapsed() }
  }
}

/**
 * Install the native radio when this launch is inside the container — **the one production caller
 * of `configureBleAdapter`.**
 *
 * Called from `main.tsx` outside React, beside `installPortraitLock()` and for the same reason: it
 * is a property of the launch rather than of any screen, it must be true before the first render
 * that asks `bleAdapter().supported()`, and it has no state a component would own. In a browser it
 * does nothing at all and the Web Bluetooth radio stays installed, so a Chrome PWA is byte-identical
 * to what P16 shipped.
 */
export function installNativeBleAdapter(configure: (adapter: BleAdapter) => void): 'native' | 'web' {
  if (!usesNativeBle()) return 'web'
  configure(nativeBleAdapter)
  return 'native'
}
